import { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"
import i18n from "i18next"
import { CheckIcon, LogOutIcon, RouteIcon, StethoscopeIcon } from "lucide-react"

import { useAgentState } from "@/agent-state"
import { AgentProfile } from "@/agent-state/use-fetch-agent-profile"
import { useAnalytics } from "@/components/core/Root/analytics.tsx"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { routesConfig } from "@/config/routes"
import { cn } from "@/helpers/classNames"
import { useAuthHook } from "@/hooks/auth"
import { useCachedRoutingProfiles } from "@/pages/Users/users-api"

import { useDiagnoseIssueModal } from "./useDiagnoseIssueModal"
import { useEditProfileModal } from "./useEditProfileModal"

/**
 * Keep the processing time when logging out if we have a voice contact in progress (ACW state)
 * in the automatic call panel
 */
const useClearPersistedStateCondition = () => {
  const { state } = useAgentState()
  const pathname = useLocation().pathname

  const isACW = state.matches({ busy: "after_call" })
  const isAutomaticCallPanel = pathname === routesConfig.AUTOMATIC_CALL_PANEL

  const withContactInProgress = isACW && isAutomaticCallPanel

  return !withContactInProgress
}

interface Props extends PropsWithChildren {
  agentEmail: string
  agentName: string
  agentProfile: AgentProfile | null
}

export function UserDropdownMenu({
  agentEmail,
  agentName,
  agentProfile,
  children,
}: Props) {
  const { t } = useTranslation()
  const { logout } = useAuthHook()
  const { resetPostHogUser } = useAnalytics()
  const { handleDiagnoseIssue } = useDiagnoseIssueModal()
  const { handleEditProfile } = useEditProfileModal(agentProfile)

  const shouldClearAllStateInLocalStorage = useClearPersistedStateCondition()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          data-testid="user-menu-trigger"
          className="
            h-[50px] rounded-md py-0
            hover:bg-transparent
          "
        >
          {children}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <UserDetailsMenuItem agentName={agentName} agentEmail={agentEmail} />
        <DropdownMenuSeparator />
        <UserRoutingProfilesMenuItem
          onEdit={handleEditProfile}
          agentProfile={agentProfile}
        />
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="py-0 text-xs">
          {t("header.language")}
        </DropdownMenuLabel>
        <LanguageMenuItem language="en">English</LanguageMenuItem>
        <LanguageMenuItem language="ja">日本語</LanguageMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleDiagnoseIssue}>
          <StethoscopeIcon className="mr-2 size-4" />
          {t("header.diagnoseIssue.title")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="logout-button"
          onClick={() => {
            logout(shouldClearAllStateInLocalStorage)
            resetPostHogUser()
          }}
        >
          <LogOutIcon className="mr-2 size-4" />
          {t("header.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function LanguageMenuItem({
  children,
  language,
}: React.PropsWithChildren<{ language: string }>) {
  const isCurrent = i18n.resolvedLanguage === language

  return (
    <DropdownMenuItem
      onClick={() => i18n.changeLanguage(language)}
      disabled={isCurrent}
    >
      <CheckIcon className={cn("mr-2 size-4", !isCurrent && "opacity-0")} />
      {children}
    </DropdownMenuItem>
  )
}

function UserDetailsMenuItem({
  agentEmail,
  agentName,
}: {
  agentEmail: string
  agentName: string
}) {
  return (
    <div className="flex w-full flex-col px-2 py-1.5">
      <span className="text-sm font-bold wrap-break-word">{agentName}</span>
      <span className="text-xs wrap-break-word text-neutral-500">
        {agentEmail}
      </span>
    </div>
  )
}

function UserRoutingProfilesMenuItem({
  agentProfile,
  onEdit,
}: {
  agentProfile: AgentProfile | null
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const { agentRoutingProfile } = useCachedRoutingProfiles(agentProfile)

  return (
    <DropdownMenuGroup className="flex flex-col items-start gap-1">
      <DropdownMenuLabel className="py-0 text-xs">
        {t("users.edit.routingProfile")}
      </DropdownMenuLabel>
      <DropdownMenuItem onClick={onEdit} className="w-full">
        <RouteIcon className="mr-2 size-4" />
        <span className="block truncate">
          {agentRoutingProfile?.name ?? "—"}
        </span>
      </DropdownMenuItem>
    </DropdownMenuGroup>
  )
}
